/* =============================================================================
 * Copyright (c) 2026 SKYXOPS Corp. All rights reserved.
 *
 * This file is proprietary and confidential. Unauthorized copying,
 * distribution, modification, or use of this file, via any medium,
 * is strictly prohibited without the express written permission of
 * SKYXOPS Corp.
 * ============================================================================= */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { Alert, AlertDescription, AlertTitle, Button, Input } from '@skyxops/ui';
import { ApiError, mfaDisable, mfaEnrollStart, mfaEnrollVerify, mfaStatus } from '../../api';

type Step = 'loading' | 'view' | 'enable-pw' | 'enable-qr' | 'disable-pw';

/**
 * Self-contained TOTP MFA management (status + enable/QR/verify + disable).
 * Renders the interactive block only — the parent supplies the card/heading.
 * Rendered in the Profile › Security section (the single Security surface).
 */
export function MfaControl() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('loading');
  const [enabled, setEnabled] = useState(false);
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [otpauth, setOtpauth] = useState('');
  const [secret, setSecret] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function refresh() {
    mfaStatus()
      .then((s) => { setEnabled(s.enabled); setStep('view'); })
      .catch((e) => {
        if (e instanceof ApiError && e.status === 401) { navigate('/login'); return; }
        setError(e instanceof Error ? e.message : String(e));
        setStep('view');
      });
  }
  // Intentionally runs once on mount. (The react-hooks plugin is not part of
  // this repo's narrow eslint config, so no disable comment is needed.)
  useEffect(refresh, []);

  function reset(next: Step) { setError(null); setNotice(null); setPassword(''); setCode(''); setStep(next); }

  async function onStart(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const r = await mfaEnrollStart(password);
      setSecret(r.secret); setOtpauth(r.otpauth_uri);
      setPassword(''); setStep('enable-qr');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not start MFA setup.');
    } finally { setBusy(false); }
  }

  async function onVerify(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await mfaEnrollVerify(code.trim());
      setSecret(''); setOtpauth(''); setCode('');
      setEnabled(true); setStep('view');
      setNotice('Two-factor authentication is now enabled.');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not verify the code.');
    } finally { setBusy(false); }
  }

  async function onDisable(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await mfaDisable(password);
      setPassword(''); setEnabled(false); setStep('view');
      setNotice('Two-factor authentication has been disabled.');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not disable MFA.');
    } finally { setBusy(false); }
  }

  const fieldLabel = 'text-sm font-medium text-foreground';
  const primaryBtn = 'bg-skyxops-midnight text-white hover:bg-skyxops-midnight/90';

  return (
    <div className="space-y-4">
      {notice && (
        <Alert className="border-skyxops-beach/40 bg-skyxops-beach/10">
          <AlertDescription className="text-sm">{notice}</AlertDescription>
        </Alert>
      )}
      {error && (
        <Alert variant="destructive">
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {step === 'loading' && <p className="text-sm text-muted-foreground">Loading…</p>}

      {step === 'view' && (
        <div className="flex items-center justify-between rounded-lg border border-border bg-muted/40 px-4 py-3">
          <div>
            <div className="text-sm font-medium">Authenticator app (TOTP)</div>
            <div className="text-xs text-muted-foreground">A one-time code from your authenticator at sign-in.</div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`text-xs font-semibold ${enabled ? 'text-skyxops-beach' : 'text-muted-foreground'}`}>
              {enabled ? 'Enabled' : 'Not enabled'}
            </span>
            {enabled ? (
              <Button variant="outline" size="sm" onClick={() => reset('disable-pw')}>Disable</Button>
            ) : (
              <Button size="sm" className={primaryBtn} onClick={() => reset('enable-pw')}>Enable</Button>
            )}
          </div>
        </div>
      )}

      {step === 'enable-pw' && (
        <form onSubmit={onStart} className="space-y-3 rounded-lg border border-border p-4">
          <div className="space-y-1.5">
            <label htmlFor="mfa-pw" className={fieldLabel}>Confirm your password to continue</label>
            <Input id="mfa-pw" type="password" autoComplete="current-password" value={password}
              onChange={(e) => setPassword(e.target.value)} disabled={busy} required autoFocus />
          </div>
          <div className="flex gap-2">
            <Button type="submit" className={primaryBtn} disabled={busy || !password}>{busy ? 'Starting…' : 'Continue'}</Button>
            <Button type="button" variant="ghost" onClick={() => reset('view')}>Cancel</Button>
          </div>
        </form>
      )}

      {step === 'enable-qr' && (
        <form onSubmit={onVerify} className="space-y-3 rounded-lg border border-border p-4">
          <p className="text-sm text-muted-foreground">Scan with Google Authenticator, Authy, or 1Password, then enter the 6-digit code.</p>
          <div className="flex justify-center rounded-lg bg-white p-4">{otpauth && <QRCodeSVG value={otpauth} size={160} />}</div>
          <p className="break-all text-center text-xs text-muted-foreground">Can’t scan? Key: <span className="font-mono">{secret}</span></p>
          <div className="space-y-1.5">
            <label htmlFor="mfa-code" className={fieldLabel}>Authentication code</label>
            <Input id="mfa-code" inputMode="numeric" autoComplete="one-time-code" placeholder="123456" maxLength={6}
              className="text-center text-lg tracking-[0.5em]" value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} disabled={busy} required autoFocus />
          </div>
          <div className="flex gap-2">
            <Button type="submit" className={primaryBtn} disabled={busy || code.length < 6}>{busy ? 'Verifying…' : 'Verify & enable'}</Button>
            <Button type="button" variant="ghost" onClick={() => reset('view')}>Cancel</Button>
          </div>
        </form>
      )}

      {step === 'disable-pw' && (
        <form onSubmit={onDisable} className="space-y-3 rounded-lg border border-border p-4">
          <div className="space-y-1.5">
            <label htmlFor="mfa-dpw" className={fieldLabel}>Confirm your password to disable two-factor</label>
            <Input id="mfa-dpw" type="password" autoComplete="current-password" value={password}
              onChange={(e) => setPassword(e.target.value)} disabled={busy} required autoFocus />
          </div>
          <div className="flex gap-2">
            <Button type="submit" variant="destructive" disabled={busy || !password}>{busy ? 'Disabling…' : 'Disable two-factor'}</Button>
            <Button type="button" variant="ghost" onClick={() => reset('view')}>Cancel</Button>
          </div>
        </form>
      )}
    </div>
  );
}
